/**
 * PATH: apps/qa/src/pages/template-detail/template-persistence.ts
 * FILE_ID: QA-TEMPLATE-PERSISTENCE-001
 * PURPOSE: Pure payload mapping plus nested template persistence orchestration.
 * WHY: Template PATCH does not write nested sections or items.
 */

import { templateApi } from '@/lib/api'
import type { TemplateItem, TemplateSection } from './types'

export function normaliseItem(item: Partial<TemplateItem>, fallbackNumber: string): TemplateItem {
  return {
    title: '', item_number: fallbackNumber, requirement_text: '', acceptance_criteria: '',
    has_hold_point: false, hold_point_config: {}, evidence_rules: {}, responsible_role: '',
    dependencies: [], risk_rating: '', is_active: true, ...item,
  }
}

export function serialiseItem(item: TemplateItem, fallbackNumber: string) {
  return {
    title: item.title, item_number: item.item_number || fallbackNumber,
    product: item.product || '', parent: item.parent || null,
    requirement_text: item.requirement_text, acceptance_criteria: item.acceptance_criteria,
    evidence_rules: item.evidence_rules, has_hold_point: item.has_hold_point,
    hold_point_config: item.hold_point_config, responsible_role: item.responsible_role,
    dependencies: item.dependencies, risk_rating: item.risk_rating, is_active: item.is_active,
  }
}

export async function persistExistingStructure(templateId: string, sections: TemplateSection[], original: TemplateSection[]) {
  const desiredSectionIds = new Set(sections.flatMap((section) => section.id ? [section.id] : []))
  await Promise.all(original.filter((section) => section.id && !desiredSectionIds.has(section.id)).map((section) => templateApi.deleteSection(section.id!)))
  for (const [sectionIndex, section] of sections.entries()) {
    const sectionPayload = {
      title: section.title, section_number: section.section_number || `${sectionIndex + 1}`,
      description: section.description || '', sort_order: sectionIndex,
    }
    const savedSection = section.id
      ? await templateApi.updateSection(section.id, sectionPayload)
      : await templateApi.createSection(templateId, sectionPayload)
    const originalItems = original.find((candidate) => candidate.id === section.id)?.items || []
    const desiredItemIds = new Set(section.items.flatMap((item) => item.id ? [item.id] : []))
    await Promise.all(originalItems.filter((item) => item.id && !desiredItemIds.has(item.id)).map((item) => templateApi.deleteItem(item.id!)))
    for (const [itemIndex, item] of section.items.entries()) {
      const payload = serialiseItem(item, `${sectionPayload.section_number}.${itemIndex + 1}`)
      if (item.id) await templateApi.updateItem(item.id, payload)
      else await templateApi.createItem(savedSection.id!, payload)
    }
  }
}

export function parseTags(value: string) {
  return value.split(',').map((tag) => tag.trim()).filter(Boolean)
}

export function itemMatches(item: TemplateItem, query: string) {
  const needle = query.trim().toLowerCase()
  if (!needle) return false
  return [item.title, item.requirement_text, item.acceptance_criteria, item.product, item.evidence_rules.instructions, item.evidence_rules.citations]
    .some((value) => String(value || '').toLowerCase().includes(needle))
}
